import { useApolloClient, useMutation } from "@apollo/client";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Image,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconCheck,
  IconClock,
  IconEdit,
  IconMessage,
  IconMoneybag,
  IconPlugConnected,
  IconX,
} from "@tabler/icons-react";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import {
  Brand,
  Collab,
  CollabStatus,
  ContentSubmissionItem,
  User,
  UserType,
} from "../../../../../gql/__generated__/graphql";
import {
  ACCEPT_COLLAB,
  ACCEPT_COLLAB_CONTENT,
  CHANGE_COLLAB_OFFER,
  DECLINE_COLLAB,
  REPLACE_CONTENT_ITEM,
  REQUEST_COLLAB_CHANGES,
  REQUEST_CONTENT_CHANGES,
  SUBMIT_COLLAB_CONTENT,
} from "../../../../apis/lambda/GraphQL/Collab/mutations";
import { GET_COLLABS_FOR_USER } from "../../../../apis/lambda/GraphQL/Collab/queries";
import { GET_MESSAGES_FOR_COLLAB } from "../../../../apis/lambda/GraphQL/Messages/queries";
import {
  CREATE_COLLAB_CHECKOUT_SESSION,
  CREATE_STRIPE_CONNECTED_ACCOUNT,
} from "../../../../apis/lambda/GraphQL/Stripe/mutations";
import { redirectToCheckout } from "../../../../apis/services/Stripe/stripeService";
import AnimationOverlay from "../../../../components/Animations/AcceptDeclineAnimation";
import GenericConfirmationModal from "../../../../components/Modals/GenericConfirmationModal";
import MessageModal from "../../../../components/Modals/MessageModal/MessageModal";
import {
  CARD_ANIMATION_SECONDS,
  COLLAB_COMPLETE_ANIMATION_SECONDS,
} from "../../../../constants/webControls";
import { BRAND_FEE_PERCENTAGE } from "../../../../constants/webData";
import { getBrandColor } from "../../../../mantineTheme";
import {
  getCollabExpirationDisplay,
  isCollabExpired,
  isCollabExpiringSoon,
} from "../../../../utils/collabExpirationUtils";
import { confettiCollabsAtom, fundedCollabIdAtom } from "../../atoms";
import CounterOfferModal from "./components/CounterOfferModal";
import ReviewContentModal from "./components/ReviewContentModal";
import SubmitContentModal from "./components/SubmitContentModal";

interface CollabCardProps {
  collab: Collab;
  userRole: UserType;
  onReview: (entity: User | Brand) => void;
}

function CollabCard(props: CollabCardProps) {
  const client = useApolloClient();

  // React State
  const [showAnimation, setShowAnimation] = useState<
    "accept" | "decline" | null
  >(null);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [counterOfferModalOpen, setCounterOfferModalOpen] = useState(false);
  const [submitContentModalOpen, setSubmitContentModalOpen] = useState(false);
  const [reviewContentModalOpen, setReviewContentModalOpen] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState<{
    opened: boolean;
    action: "accept" | "decline";
  }>({ opened: false, action: "accept" });
  const [stripeConnectModalOpen, setStripeConnectModalOpen] = useState(false);

  // Jotai State
  const setConfettiCollabs = useSetAtom(confettiCollabsAtom);
  const fundedCollabId = useAtomValue(fundedCollabIdAtom);

  useEffect(() => {
    if (fundedCollabId === props.collab.id) {
      setShowAnimation("accept");
      const timer = setTimeout(() => {
        setShowAnimation(null);
      }, CARD_ANIMATION_SECONDS * 1000);
      return () => clearTimeout(timer);
    }
  }, [fundedCollabId, props.collab.id]);

  // GraphQL
  const [acceptCollab] = useMutation(ACCEPT_COLLAB);
  const [declineCollab] = useMutation(DECLINE_COLLAB);
  const [changeCollabOffer] = useMutation(CHANGE_COLLAB_OFFER);
  const [submitContent] = useMutation(SUBMIT_COLLAB_CONTENT);
  const [acceptCollabContent] = useMutation(ACCEPT_COLLAB_CONTENT);
  const [requestChanges] = useMutation(REQUEST_COLLAB_CHANGES);
  const [requestContentChanges] = useMutation(REQUEST_CONTENT_CHANGES);
  const [replaceContentItem] = useMutation(REPLACE_CONTENT_ITEM);
  const [createCollabCheckoutSession, { loading: checkoutLoading }] =
    useMutation(CREATE_COLLAB_CHECKOUT_SESSION);
  const [createStripeConnectedAccount, { loading: stripeConnectLoading }] =
    useMutation(CREATE_STRIPE_CONNECTED_ACCOUNT);

  const isActionRequired =
    (props.userRole === "BRAND_USER" &&
      props.collab.actionRequiredBy === "BRAND_USER") ||
    (props.userRole === "CREATOR" &&
      props.collab.actionRequiredBy === "CREATOR");

  const canMakeCounterOffer =
    props.collab.submittedBy !== props.userRole &&
    (props.collab.status === "PENDING" || props.collab.status === "DISCUSSING");

  const getStatusColor = (status: CollabStatus) => {
    switch (status) {
      case "PENDING":
        return "tertiary";
      case "ACCEPTED":
        return "secondary";
      case "AWAITING_FUNDING":
        return "orange";
      case "AWAITING_TRANSFER":
        return "yellow";
      case "DISCUSSING":
        return "blue";
      case "COMPLETED":
        return "primary";
      case "DECLINED":
        return "red";
      case "CONTENT_SUBMITTED":
        return "grape";
      case "CHANGES_REQUESTED":
        return "yellow";
      case "EXPIRED":
        return "gray";
      default:
        return "gray";
    }
  };

  const formatRequirements = (collab: Collab) => {
    return collab.requirements
      .map(
        (req) =>
          `${req.quantity} ${
            req.type.charAt(0).toUpperCase() + req.type.slice(1).toLowerCase()
          }${req.quantity > 1 ? "s" : ""}`
      )
      .join(", ");
  };

  // Handle message button click
  const handleMessageClick = () => {
    setMessageModalOpen(true);
  };

  // Handle counter offer button click
  const handleCounterOfferClick = () => {
    setCounterOfferModalOpen(true);
  };

  const handleAcceptContent = async (contentId: string) => {
    try {
      const { data } = await acceptCollabContent({
        variables: {
          collabId: props.collab.id,
          contentId,
        },
      });
      notifications.show({
        title: "Content Accepted",
        message: "The content has been approved.",
        color: "green",
      });

      if (data?.acceptCollabContent.isAllContentApproved) {
        const creator = props.collab.creator;
        setConfettiCollabs(true); // Trigger confetti
        setShowAnimation("accept"); // Trigger success animation
        setReviewContentModalOpen(false); // Close content review modal

        setTimeout(() => {
          setShowAnimation(null); // Clear animation state
          setConfettiCollabs(false); // Stop confetti
          if (creator) {
            props.onReview(creator);
          }
          client.refetchQueries({ include: [GET_COLLABS_FOR_USER] });
        }, COLLAB_COMPLETE_ANIMATION_SECONDS * 1000);
      }
    } catch (error) {
      console.error("Error accepting content:", error);
      notifications.show({
        title: "Error",
        message: "There was an error accepting the content. Please try again.",
        color: "red",
      });
    }
  };

  const handleRequestChanges = async (message?: string) => {
    try {
      await requestChanges({
        variables: {
          collabId: props.collab.id,
          message,
        },
      });

      setReviewContentModalOpen(false);
      setMessageModalOpen(true);

      notifications.show({
        title: "Changes Requested",
        message: "The creator has been notified of the requested changes.",
        color: "yellow",
      });

      // Refetch collabs to get updated status
      client.refetchQueries({ include: [GET_COLLABS_FOR_USER] });
    } catch (error) {
      console.error("Error requesting changes:", error);
      notifications.show({
        title: "Error",
        message: "There was an error requesting changes. Please try again.",
        color: "red",
      });
    }
  };

  const handleRequestContentChanges = async (
    contentId: string,
    feedbackMessage: string
  ) => {
    try {
      await requestContentChanges({
        variables: {
          collabId: props.collab.id,
          contentId,
          feedbackMessage,
        },
      });

      notifications.show({
        title: "Changes Requested",
        message: "The creator has been notified of the requested changes for this content.",
        color: "yellow",
      });

      // Refetch collabs to get updated status
      client.refetchQueries({ include: [GET_COLLABS_FOR_USER] });
    } catch (error) {
      console.error("Error requesting content changes:", error);
      notifications.show({
        title: "Error",
        message: "There was an error requesting changes. Please try again.",
        color: "red",
      });
    }
  };

  const handleReplaceContent = async (contentId: string, newContentUrl: string) => {
    try {
      // Determine content type from URL
      const isVideo = newContentUrl.includes("tiktok.com") ||
                      newContentUrl.includes("youtube.com") ||
                      newContentUrl.includes("video");

      await replaceContentItem({
        variables: {
          collabId: props.collab.id,
          contentId,
          newContentUrl,
          newContentType: isVideo ? "video" : "image",
        },
      });

      notifications.show({
        title: "Content Replaced",
        message: "Your content has been successfully replaced and is now pending review.",
        color: "green",
      });

      // Refetch collabs to get updated status
      client.refetchQueries({ include: [GET_COLLABS_FOR_USER] });
    } catch (error) {
      console.error("Error replacing content:", error);
      notifications.show({
        title: "Error",
        message: "There was an error replacing the content. Please try again.",
        color: "red",
      });
    }
  };

  const handleSubmitContent = async (data: {
    contentItems: ContentSubmissionItem[];
    message?: string;
  }) => {
    try {
      await submitContent({
        variables: {
          input: {
            collabId: props.collab.id,
            contentItems: data.contentItems,
            message: data.message,
          },
        },
      });

      setSubmitContentModalOpen(false);

      notifications.show({
        title: "Content submitted",
        message: "Your content has been submitted successfully.",
        color: "secondary",
      });

      // Refetch collabs to get updated data
      client.refetchQueries({
        include: [GET_COLLABS_FOR_USER, GET_MESSAGES_FOR_COLLAB],
      });
    } catch (error) {
      console.error("Error submitting content:", error);
      notifications.show({
        title: "Error submitting content",
        message: "Please try again later or contact us.",
        color: "red",
      });
    }
  };

  const openConfirmationModal = (action: "accept" | "decline") => {
    setConfirmationModal({
      opened: true,
      action,
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal({
      ...confirmationModal,
      opened: false,
    });
  };

  const handleConfirmAction = async () => {
    const action = confirmationModal.action;
    closeConfirmationModal();

    if (action === "accept") {
      try {
        const { data } = await acceptCollab({
          variables: { collabId: props.collab.id },
        });

        if (props.userRole === "BRAND_USER") {
          const checkoutId = data?.acceptCollab;
          if (checkoutId) {
            redirectToCheckout(checkoutId);
          } else {
            notifications.show({
              title: "Collab Accepted",
              message:
                "There was an issue creating the funding page. You can fund this collab from the 'Accepted' tab.",
              color: "yellow",
            });
            client.refetchQueries({ include: [GET_COLLABS_FOR_USER] });
          }
        } else {
          setShowAnimation("accept");
          setTimeout(() => {
            setShowAnimation(null);
            client.refetchQueries({ include: [GET_COLLABS_FOR_USER] });
          }, CARD_ANIMATION_SECONDS * 1000);
        }
      } catch (error) {
        console.error("Error accepting collab:", error);
        notifications.show({
          title: "Error accepting collab",
          message: "Please try again later or contact us.",
          color: "red",
        });
      }
    } else if (action === "decline") {
      try {
        await declineCollab({
          variables: { collabId: props.collab.id },
        });
        setShowAnimation("decline");
        setTimeout(() => {
          setShowAnimation(null);
          client.refetchQueries({ include: [GET_COLLABS_FOR_USER] });
        }, CARD_ANIMATION_SECONDS * 1000);
      } catch (error) {
        console.error("Error declining collab:", error);
        notifications.show({
          title: "Error declining collab",
          message: "Please try again later or contact us.",
          color: "red",
        });
      }
    }
  };

  const handleFundNow = async () => {
    try {
      const { data } = await createCollabCheckoutSession({
        variables: {
          collabId: props.collab.id,
        },
      });

      console.log("Checkout session data:", data);

      if (data?.createCollabFundingCheckoutSession) {
        redirectToCheckout(data.createCollabFundingCheckoutSession);
      } else {
        notifications.show({
          title: "Error",
          message: "Could not create a checkout session. Please try again.",
          color: "red",
        });
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      notifications.show({
        title: "Error",
        message: "There was an error. Please try again.",
        color: "red",
      });
    }
  };

  const handleConnectStripe = async () => {
    setStripeConnectModalOpen(false);
    try {
      const response = await createStripeConnectedAccount();
      const url = response.data?.createStripeConnectedAccount;
      if (url) {
        window.location.href = url;
      } else {
        notifications.show({
          title: "Error",
          message: "Could not create Stripe connection. Please try again.",
          color: "red",
        });
      }
    } catch (error) {
      console.error("Error creating Stripe connected account:", error);
      notifications.show({
        title: "Error",
        message: "There was an error connecting to Stripe. Please try again.",
        color: "red",
      });
    }
  };

  const handleCounterOfferSubmit = async (
    newPrice: number,
    message?: string
  ) => {
    try {
      await changeCollabOffer({
        variables: {
          input: {
            collabId: props.collab.id,
            price: newPrice,
            message: message,
          },
        },
      });

      setCounterOfferModalOpen(false);

      notifications.show({
        title: "Counter offer sent",
        message: "Your counter offer has been sent successfully.",
        color: "secondary",
      });

      // Refetch collabs to get updated data
      client.refetchQueries({
        include: [GET_COLLABS_FOR_USER],
      });
    } catch (error) {
      console.error("Error sending counter offer:", error);
      notifications.show({
        title: "Error sending counter offer",
        message: "Please try again later or contact us.",
        color: "red",
      });
    }
  };

  const entityName =
    props.userRole === "BRAND_USER"
      ? props.collab.creatorEmail
      : props.collab.brandId;

  const acceptButtonText =
    props.userRole === "BRAND_USER" ? "Accept & Fund" : "Accept";

  return (
    <Box data-component="CollabsCard">
      <Card
        withBorder
        padding="md"
        pt={isActionRequired ? "24px" : "md"}
        radius="md"
        h="100%"
        display="flex"
        style={{
          flexDirection: "column",
          borderLeft: isActionRequired
            ? `4px solid ${getBrandColor("primary")}`
            : undefined,
          backgroundColor: isActionRequired ? "#fff9f5" : undefined,
          position: "relative",
          overflow: "hidden", // Ensure animation stays within card
        }}
      >
        {/* Animation overlay using the reusable component */}
        <AnimationOverlay
          type={showAnimation === "accept" ? "success" : "error"}
          isVisible={showAnimation !== null}
        />

        {isActionRequired && (
          <Badge
            color="primary"
            variant="filled"
            size="sm"
            style={{ position: "absolute", top: 4, right: 10, zIndex: 2 }}
          >
            Action Required
          </Badge>
        )}

        <Stack gap="xs" style={{ flex: 1 }}>
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <Text fw={700} size="md" lineClamp={1}>
              {props.collab.products?.map((product) => product.name).join(", ")}
            </Text>
            <Stack gap={4} align="flex-end">
              <Badge color={getStatusColor(props.collab.status)}>
                {props.collab.status.charAt(0).toUpperCase() +
                  props.collab.status.slice(1).toLowerCase().replace("_", " ")}
              </Badge>
              <Text size="xs" c="dimmed">
                Updated {new Date(props.collab.updatedAt).toLocaleDateString()}
              </Text>
              {getCollabExpirationDisplay(props.collab) && (
                <Group gap={4}>
                  {isCollabExpired(props.collab) ? (
                    <IconAlertCircle size={12} color="red" />
                  ) : isCollabExpiringSoon(props.collab) ? (
                    <IconClock size={12} color="orange" />
                  ) : (
                    <IconClock size={12} color="gray" />
                  )}
                  <Text
                    size="xs"
                    c={
                      isCollabExpired(props.collab)
                        ? "red"
                        : isCollabExpiringSoon(props.collab)
                        ? "orange"
                        : "dimmed"
                    }
                  >
                    {getCollabExpirationDisplay(props.collab)}
                  </Text>
                </Group>
              )}
            </Stack>
          </Group>

          <Flex gap="md" align="flex-start" wrap="nowrap">
            {props.collab.products && props.collab.products.length > 0 && (
              <Box style={{ width: 80, height: 80, flexShrink: 0 }}>
                {props.collab.products.length === 1 ? (
                  <Image
                    src={props.collab.products[0]?.imageUrl}
                    alt={props.collab.products[0]?.name}
                    radius="md"
                    height={80}
                    width={80}
                    fit="cover"
                    style={{ display: "block" }}
                  />
                ) : (
                  <Group
                    gap={2}
                    wrap="wrap"
                    style={{ height: 80, width: 80, overflow: "hidden" }}
                  >
                    {props.collab.products.slice(0, 4).map((product, index) => {
                      const imageSize =
                        props.collab.products!.length === 2 ? 39 : 38;
                      return (
                        <Image
                          key={product.id || index}
                          src={product.imageUrl}
                          alt={product.name}
                          radius="sm"
                          height={imageSize}
                          width={imageSize}
                          fit="cover"
                          style={{ display: "block", flexShrink: 0 }}
                        />
                      );
                    })}
                  </Group>
                )}
              </Box>
            )}

            <Stack gap="xs" style={{ flex: "1 1 0%", minWidth: 0 }}>
              {props.collab.brand && (
                <Group gap="xs" wrap="nowrap">
                  <Avatar
                    src={props.collab.brand.logo}
                    size="xs"
                    radius="xl"
                    style={{ flexShrink: 0 }}
                  />
                  <Text
                    size="sm"
                    fw={500}
                    lineClamp={1}
                    style={{ minWidth: 0 }}
                  >
                    {props.collab.brand.name}
                  </Text>
                </Group>
              )}

              {props.collab.creator && (
                <Group gap="xs" wrap="nowrap">
                  <Avatar
                    src={props.collab.creator.imageUrl}
                    size="xs"
                    radius="xl"
                    style={{ flexShrink: 0 }}
                  />
                  <Text
                    size="sm"
                    fw={500}
                    lineClamp={1}
                    style={{ minWidth: 0 }}
                  >
                    {props.collab.creator.name}
                  </Text>
                </Group>
              )}

              <Group>
                <Text fw={600} size="md" c="secondary.6">
                  ${props.collab.price.toLocaleString()}
                </Text>
              </Group>

              <Text size="xs" c="dimmed" lineClamp={1}>
                for {formatRequirements(props.collab)}
              </Text>

              {props.collab.unreadMessageCount > 0 && (
                <Badge color="red" variant="light" size="sm">
                  {props.collab.unreadMessageCount} new message
                  {props.collab.unreadMessageCount > 1 ? "s" : ""}
                </Badge>
              )}
            </Stack>
          </Flex>
        </Stack>

        <Divider my="md" />

        <Group justify="flex-end" gap="sm">
          {(props.collab.status === "PENDING" ||
            props.collab.status === "DISCUSSING") &&
            props.collab.submittedBy !== props.userRole && (
              <>
                <Button
                  variant="outline"
                  color="red"
                  size="xs"
                  leftSection={<IconX size={14} />}
                  onClick={() => openConfirmationModal("decline")}
                >
                  Decline
                </Button>
                <Button
                  variant="filled"
                  color="secondary"
                  size="xs"
                  leftSection={<IconCheck size={14} />}
                  onClick={() => openConfirmationModal("accept")}
                >
                  {acceptButtonText}
                </Button>
              </>
            )}

          {props.collab.status === "AWAITING_FUNDING" &&
            props.userRole === "BRAND_USER" && (
              <Button
                variant="filled"
                color="secondary"
                size="xs"
                leftSection={<IconMoneybag size={14} />}
                onClick={handleFundNow}
                loading={checkoutLoading}
              >
                Fund Now
              </Button>
            )}

          {props.collab.status === "AWAITING_TRANSFER" &&
            props.userRole === "CREATOR" && (
              <Button
                variant="filled"
                color="yellow"
                size="xs"
                leftSection={<IconPlugConnected size={14} />}
                onClick={() => setStripeConnectModalOpen(true)}
                loading={stripeConnectLoading}
              >
                Connect with Stripe
              </Button>
            )}

          {props.collab.status === "ACCEPTED" &&
            props.userRole === "CREATOR" && (
              <Button
                variant="filled"
                color="secondary"
                size="xs"
                leftSection={<IconCheck size={14} />}
                onClick={() => setSubmitContentModalOpen(true)}
              >
                Submit Content
              </Button>
            )}

          {props.collab.status === "CHANGES_REQUESTED" &&
            props.userRole === "CREATOR" && (
              <Button
                variant="filled"
                color="yellow"
                size="xs"
                leftSection={<IconEdit size={14} />}
                onClick={() => {
                  // If content already exists, open review modal to allow replacing specific items
                  if (props.collab.submittedContent && props.collab.submittedContent.length > 0) {
                    setReviewContentModalOpen(true);
                  } else {
                    setSubmitContentModalOpen(true);
                  }
                }}
              >
                Resubmit Content
              </Button>
            )}

          {props.collab.status === "CONTENT_SUBMITTED" &&
            props.userRole === "BRAND_USER" && (
              <Button
                variant="filled"
                color="grape"
                size="xs"
                onClick={() => setReviewContentModalOpen(true)}
              >
                Review Content
              </Button>
            )}

          {canMakeCounterOffer && (
            <Button
              variant="outline"
              color="primary"
              size="xs"
              leftSection={<IconEdit size={14} />}
              onClick={handleCounterOfferClick}
            >
              Counter Offer
            </Button>
          )}

          {props.collab.status === "COMPLETED" && (
            <Button
              variant="filled"
              color="primary"
              size="xs"
              onClick={() => {
                const reviewTarget =
                  props.userRole === "BRAND_USER"
                    ? props.collab.creator
                    : props.collab.brand;
                if (reviewTarget) {
                  props.onReview(reviewTarget);
                }
              }}
            >
              Leave Review
            </Button>
          )}

          <Button
            variant={props.collab.status === "PENDING" ? "subtle" : "filled"}
            color="primary"
            size="xs"
            leftSection={<IconMessage size={14} />}
            onClick={handleMessageClick}
          >
            {props.collab.unreadMessageCount > 0
              ? `Messages (${props.collab.unreadMessageCount})`
              : "Message"}
          </Button>
        </Group>
      </Card>

      {/* Confirmation Modal */}
      <GenericConfirmationModal
        opened={confirmationModal.opened}
        onClose={closeConfirmationModal}
        title={
          props.userRole === "BRAND_USER" &&
          confirmationModal.action === "accept"
            ? "Accept & Fund Collab"
            : "Confirm Action"
        }
        action={confirmationModal.action}
        text={
          <Stack>
            <Text>
              {props.userRole === "BRAND_USER" &&
              confirmationModal.action === "accept" ? (
                <>
                  Please confirm you want to accept this collab. You will be
                  redirected to fund the collab:
                  <Stack gap="xs" mt="sm">
                    <Group justify="space-between">
                      <Text size="sm">Collaboration Amount:</Text>
                      <Text size="sm" fw={600}>
                        ${props.collab.price.toLocaleString()}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm">Platform Fee (1%):</Text>
                      <Text size="sm" fw={600}>
                        $
                        {(props.collab.price * BRAND_FEE_PERCENTAGE).toFixed(2)}
                      </Text>
                    </Group>
                    <Divider />
                    <Group justify="space-between">
                      <Text fw={700}>Total Charge:</Text>
                      <Text fw={700} c="secondary.6">
                        $
                        {(
                          props.collab.price *
                          (1 + BRAND_FEE_PERCENTAGE)
                        ).toFixed(2)}
                      </Text>
                    </Group>
                  </Stack>
                  <Text size="sm" c="dimmed" mt="md" fs="italic">
                    This is a deposit. Funds will be fully refunded if the creator doesn't complete the collaboration.
                  </Text>
                </>
              ) : (
                <>
                  Are you sure you want to {confirmationModal.action} the collab
                  from{" "}
                  <Text span fw={600}>
                    {entityName}
                  </Text>{" "}
                  for{" "}
                  <Text span fw={600}>
                    ${props.collab.price.toLocaleString()}
                  </Text>
                  ?
                </>
              )}
            </Text>

            <Text size="sm" c="dimmed">
              Product:{" "}
              {props.collab.products?.map((product) => product.name).join(", ")}
            </Text>
          </Stack>
        }
        onConfirm={handleConfirmAction}
        confirmText={
          props.userRole === "BRAND_USER" &&
          confirmationModal.action === "accept"
            ? "Accept & Proceed to Funding"
            : `Yes, ${confirmationModal.action}`
        }
      />

      {/* Message Modal */}
      <MessageModal
        opened={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        collab={props.collab}
        userType={props.userRole}
      />

      {/* Counter Offer Modal */}
      <CounterOfferModal
        opened={counterOfferModalOpen}
        onClose={() => setCounterOfferModalOpen(false)}
        collab={props.collab}
        userRole={props.userRole}
        onSubmit={handleCounterOfferSubmit}
      />

      {/* Submit Content Modal */}
      <SubmitContentModal
        opened={submitContentModalOpen}
        onClose={() => setSubmitContentModalOpen(false)}
        collab={props.collab}
        onSubmit={handleSubmitContent}
      />

      {/* Review Content Modal */}
      <ReviewContentModal
        opened={reviewContentModalOpen}
        onClose={() => setReviewContentModalOpen(false)}
        collab={props.collab}
        userRole={props.userRole}
        onAccept={handleAcceptContent}
        onRequestChanges={handleRequestChanges}
        onRequestContentChanges={handleRequestContentChanges}
        onReplaceContent={handleReplaceContent}
      />

      {/* Stripe Connect Modal */}
      <GenericConfirmationModal
        opened={stripeConnectModalOpen}
        onClose={() => setStripeConnectModalOpen(false)}
        title="Connect with Stripe"
        text={
          <Text>
            To withdraw your funds, you need to provide some information to Stripe, our payment platform.
            Please fill out all of the information completely and link your bank account so Stripe can
            transfer your hard-earned cash directly to you!
          </Text>
        }
        onConfirm={handleConnectStripe}
        confirmText="Continue to Stripe"
      />
    </Box>
  );
}

export default CollabCard;
